import React from "react";
import { useNavigate } from "react-router-dom";

import type { ApprovedArtifactVersion } from "./processingTypes";
import { formatDateTime } from "./processingDisplay";
import { JobRef } from "./JobRefBadge";
import { EntityRefBadge } from "../../components/EntityRef/EntityRefBadge";
import { playbackObjectRemovalDisplay } from "./playbackObjectRemovalDisplay";
import { songPlayHistoryEventLabel } from "./songPlayHistoryLabels";
import styles from "./LineagePanel.module.scss";

// Song Play History — one current Song Audio (KrossKast Playback MP3) +
// read-only prior processing/replacement events. No Revoke actions.

function supersededVersionLabel(
  artifact: ApprovedArtifactVersion,
  byId: Map<string, ApprovedArtifactVersion>,
): string {
  const by = artifact.supersededByArtifactVersionId
    ? byId.get(artifact.supersededByArtifactVersionId)
    : undefined;
  if (by) return `v${by.version}`;
  if (artifact.supersededByArtifactVersionId) {
    return artifact.supersededByArtifactVersionId;
  }
  return "—";
}

function MetaField({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className={styles.metaField}>
      <label>{label}</label>
      <div className={mono ? styles.mono : undefined}>{children}</div>
    </div>
  );
}

export interface LineagePanelProps {
  trackId: string;
  artifactVersions: ApprovedArtifactVersion[];
  /** Highlights the version produced by the job being viewed. */
  currentJobId?: string;
}

const LineagePanel: React.FC<LineagePanelProps> = ({
  trackId,
  artifactVersions,
  currentJobId,
}) => {
  const navigate = useNavigate();
  const byId = new Map(
    artifactVersions.map((a) => [a.artifactVersionId, a] as const),
  );
  const current =
    artifactVersions.find((a) => a.status === "active") ||
    [...artifactVersions].sort((a, b) => b.version - a.version)[0] ||
    null;
  const previous = [...artifactVersions]
    .filter((a) => a.artifactVersionId !== current?.artifactVersionId)
    .sort((a, b) => b.version - a.version);

  const openJob = (jobId: string) =>
    navigate(`/processing-jobs/${encodeURIComponent(jobId)}`);

  const cleanupAttention = previous
    .map((a) => playbackObjectRemovalDisplay(a))
    .filter((d) => d && d.attention);

  return (
    <div className={styles.lineage}>
      <div className={styles.trackNode}>
        <i className="bi bi-music-note-beamed" aria-hidden="true" />
        <span className={styles.trackLabel}>Song</span>
        <EntityRefBadge kind="track" id={trackId} />
      </div>

      {cleanupAttention.length > 0 ? (
        <div className={styles.cleanupWarning} role="status">
          Storage cleanup needs attention for {cleanupAttention.length} prior
          Song Audio object
          {cleanupAttention.length === 1 ? "" : "s"}. The current Song Audio is
          unaffected.
        </div>
      ) : null}

      <section className={styles.section} aria-labelledby="current-playback-heading">
        <h4 id="current-playback-heading" className={styles.sectionHeading}>
          Current Song Audio
        </h4>
        <p className={styles.sectionHelp}>
          The current KrossKast Playback MP3 attached to this Song. Reprocess /
          Replace Song Audio creates a validated replacement; only then does this
          pointer move.
        </p>
        {!current ? (
          <p className={styles.emptyNote}>No current Song Audio on record.</p>
        ) : (
          <div
            className={`${styles.versionCard} ${styles.status_active} ${
              currentJobId && current.sourceProcessingJobId === currentJobId
                ? styles.thisJob
                : ""
            }`}
          >
            <div className={styles.versionHeader}>
              <span className={styles.versionNumber}>
                {songPlayHistoryEventLabel(current, { isCurrent: true })} · v
                {current.version}
              </span>
              <span className={`${styles.statusBadge} ${styles.badge_active}`}>
                {current.status === "active" ? "Active" : current.status}
              </span>
              {currentJobId && current.sourceProcessingJobId === currentJobId ? (
                <span className={styles.currentJobTag}>this job</span>
              ) : null}
            </div>
            <div className={styles.metaGrid}>
              <MetaField label="Created">{formatDateTime(current.createdAt)}</MetaField>
              <MetaField label="Current Song Processing ID">
                <JobRef
                  jobId={current.sourceProcessingJobId}
                  onOpen={
                    current.sourceProcessingJobId
                      ? () => openJob(current.sourceProcessingJobId)
                      : undefined
                  }
                />
              </MetaField>
              <MetaField label="Checksum" mono>
                {current.checksumApproved || "—"}
              </MetaField>
              <MetaField label="Processing recipe" mono>
                {current.ffmpegRecipeId || "—"}
              </MetaField>
              <MetaField label="Playback location" mono>
                {current.approvedObjectKey || "—"}
              </MetaField>
              <MetaField label="Active status">
                {current.status === "active"
                  ? "Yes — current Song Audio"
                  : current.status}
              </MetaField>
            </div>
          </div>
        )}
      </section>

      <section className={styles.section} aria-labelledby="previous-events-heading">
        <h4 id="previous-events-heading" className={styles.sectionHeading}>
          Previous Processing Events
        </h4>
        <p className={styles.sectionHelp}>
          Read-only audit of Song Audio replacements. Prior binaries are removed
          after verified cutover; checksum and processing evidence remain. These
          entries are not independently playable.
        </p>
        {previous.length === 0 ? (
          <p className={styles.emptyNote}>No prior processing events.</p>
        ) : (
          <ul className={styles.eventList}>
            {previous.map((artifact) => {
              const isCurrentJob =
                currentJobId && artifact.sourceProcessingJobId === currentJobId;
              const removal = playbackObjectRemovalDisplay(artifact);
              return (
                <li key={artifact.artifactVersionId} className={styles.eventItem}>
                  <div
                    className={`${styles.versionCard} ${
                      styles[`status_${artifact.status}`] ?? ""
                    } ${removal?.attention ? styles.cleanupAttention : ""}`}
                  >
                    <div className={styles.versionHeader}>
                      <span className={styles.versionNumber}>
                        {songPlayHistoryEventLabel(artifact)} · v{artifact.version}
                      </span>
                      <span
                        className={`${styles.statusBadge} ${
                          styles[`badge_${artifact.status}`] ?? ""
                        }`}
                      >
                        {artifact.status === "superseded"
                          ? "Superseded"
                          : artifact.status === "revoked"
                            ? "Revoked (legacy)"
                            : artifact.status}
                      </span>
                      {isCurrentJob ? (
                        <span className={styles.currentJobTag}>this job</span>
                      ) : null}
                    </div>
                    <div className={styles.metaGrid}>
                      <MetaField label="Date replaced">
                        {artifact.supersededAt
                          ? formatDateTime(artifact.supersededAt)
                          : artifact.revokedAt
                            ? formatDateTime(artifact.revokedAt)
                            : "—"}
                      </MetaField>
                      <MetaField label="Processing ID">
                        <JobRef
                          jobId={artifact.sourceProcessingJobId}
                          onOpen={
                            artifact.sourceProcessingJobId
                              ? () => openJob(artifact.sourceProcessingJobId)
                              : undefined
                          }
                        />
                      </MetaField>
                      <MetaField label="Recipe" mono>
                        {artifact.ffmpegRecipeId || "—"}
                      </MetaField>
                      <MetaField label="Checksum" mono>
                        {artifact.checksumApproved || "—"}
                      </MetaField>
                      <MetaField label="Replaced by version">
                        {supersededVersionLabel(artifact, byId)}
                      </MetaField>
                      <MetaField label="Superseded MP3 removal">
                        <span
                          className={
                            removal?.attention ? styles.removalAttention : undefined
                          }
                        >
                          {removal?.label || "—"}
                        </span>
                        {artifact.playbackObjectRemovedAt
                          ? ` (${formatDateTime(artifact.playbackObjectRemovedAt)})`
                          : ""}
                        {artifact.playbackObjectRemoveError && removal?.attention ? (
                          <div className={styles.subtle}>
                            {String(artifact.playbackObjectRemoveError)}
                          </div>
                        ) : null}
                      </MetaField>
                      <MetaField label="Historical object key" mono>
                        {artifact.approvedObjectKey || "—"}
                      </MetaField>
                      <MetaField label="Audit record" mono>
                        {artifact.artifactVersionId}
                      </MetaField>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default LineagePanel;
